/* ------------------------------------------------------------------ *
 * Uitleg bij elke kengetal op de TWR- en Risk-pagina (gebruikt door
 * InfoTip.js). Velden:
 *   title      kop van de uitleg
 *   what       wat het getal laat zien
 *   direction  'higher' | 'lower' | 'zero' | 'within' | 'info'  (wat is goed?)
 *              'zero' = getal met minteken (een daling): dichter bij 0% is beter
 *   good       toelichting bij wat goed/slecht is
 *   example    uitgewerkt voorbeeld met getallen
 *   benchmark  marktrichtlijn / vergelijkingsmateriaal (indicatief)
 *   note       let op / valkuil
 * Marktrichtlijnen zijn vuistregels, geen beleggingsadvies.
 * ------------------------------------------------------------------ */

export const GLOSSARY = {
  /* ------------------------------ TWR-pagina ------------------------------ */
  twr_selected: {
    title: 'TWR van de gekozen periode',
    what: 'Het rendement van je beleggingen over de periode die je hebt gekozen, schoongemaakt voor stortingen en opnames (time-weighted return). 0% = precies gelijk gebleven, +10% = je portefeuille is 10% gegroeid door beleggen.',
    direction: 'higher',
    good: 'Positief is winst, negatief is verlies. Vergelijk altijd met een periode van dezelfde lengte.',
    benchmark: 'De brede aandelenmarkt (S&P 500) geeft op lange termijn ongeveer 8–10% per jaar, met jaren van −20% tot +30%. Optieverkopers mikken vaak op 10–20% per jaar; structureel boven 20% is uitzonderlijk en gaat meestal samen met meer risico.',
    note: 'Korte periodes (een maand) zeggen weinig over je kunnen; kijk ook naar volatiliteit en drawdown.',
  },
  twr_all: {
    title: 'Totaal-TWR (alle historie)',
    what: 'Het totale rendement sinds het begin van de beschikbare data, zonder effect van stortingen en opnames. Het is niet geannualiseerd: het is het cumulatieve resultaat.',
    direction: 'higher',
    good: 'Hoe hoger, hoe beter, maar het hangt af van hoe lang de periode is — zie de kaart "Volledige historie".',
    benchmark: 'Reken om naar een jaarcijfer om te vergelijken: ~9% per jaar is gebruikelijk voor de S&P 500.',
  },
  twr_ytd: {
    title: 'YTD-TWR (dit jaar)',
    what: 'Rendement sinds 1 januari van dit jaar, zonder effect van stortingen en opnames.',
    direction: 'higher',
    good: 'Vergelijk met je jaardoel onder Equity → Goals.',
    benchmark: 'S&P 500: gemiddeld ~9% per jaar, maar een jaar kan tussen ongeveer −25% en +30% uitkomen.',
  },
  twr_1y: {
    title: '1-jaars TWR',
    what: 'Rendement over de afgelopen 12 maanden (rollend), zonder effect van stortingen en opnames.',
    direction: 'higher',
    good: 'Een volledig jaar filtert seizoenseffecten weg en is daardoor het eerlijkste vergelijkingsmoment.',
    benchmark: 'S&P 500: ~8–10% per jaar gemiddeld; spaarrente/staatsobligaties 2–4% als risicovrije maatstaf.',
  },
  twr_history: {
    title: 'Volledige historie',
    what: 'De begin- en einddatum van de data waarop de TWR is berekend.',
    direction: 'info',
    good: 'Hoe langer de historie, hoe betrouwbaarder de statistieken (Sharpe, Calmar, max drawdown).',
    note: 'Begint de historie later dan je account, dan ontbreken stortingsgegevens van eerder; zie de gele melding.',
  },
  volatility: {
    title: 'Volatiliteit (jaarbasis)',
    what: 'Hoeveel je rendement op en neer schommelt. Het is de standaarddeviatie van je dagelijkse rendementen, omgerekend naar een jaar (×√252). 30% betekent: in een gewoon jaar ligt je uitkomst ongeveer 30 procentpunt rond het gemiddelde.',
    direction: 'lower',
    good: 'Lager = rustiger. Hoge volatiliteit is niet per se slecht als het rendement daar ruim tegenover staat (dat meet de Sharpe-ratio).',
    benchmark: 'Obligaties 4–6%, brede aandelenindex (S&P 500) ongeveer 15–20%, individuele groei-/techaandelen 30–60%. Onder 15% is laag, 15–25% gemiddeld, boven 30% hoog.',
    note: 'Een portefeuille met veel volatiele namen (NBIS, IONQ, ...) en geleend geld zit al snel op 40–60%.',
  },
  sharpe: {
    title: 'Sharpe-ratio',
    what: 'Rendement boven de risicovrije rente, gedeeld door de volatiliteit. Het antwoord op: hoeveel extra rendement krijg ik per eenheid risico dat ik loop?',
    direction: 'higher',
    good: 'Onder 0: slechter dan sparen. 0–1: matig. 1–2: goed. Boven 2: zeer goed — en over korte tijd vaak toeval.',
    benchmark: 'S&P 500 haalt op lange termijn ongeveer 0,4–0,6. Professionele fondsen scoren 0,5–1,5 over meerdere jaren.',
    note: 'Heeft minimaal 10 dagen data nodig en is over een maand of kwartaal erg wisselvallig. Pas het veld "Risk-free %" aan naar de actuele rente (spaarrente of 3-maands staatsobligatie); 0 laat de rente buiten beschouwing.',
  },
  max_drawdown: {
    title: 'Maximale drawdown',
    what: 'De grootste daling van een top naar het daaropvolgende dal in deze periode. Het laat zien hoeveel pijn je op het slechtste moment hebt moeten verdragen.',
    example: 'De lijn stijgt naar 120 (top), zakt naar 90 (dal) en herstelt daarna naar 110. De maximale drawdown is 90 ÷ 120 − 1 = −25%, ook al sta je nu nog maar 110 ÷ 120 − 1 = −8,3% onder de top.',
    direction: 'zero',
    good: 'Het getal staat met een minteken omdat het een daling is. Een kleinere min is beter: −10% is beter dan −30%, en 0% is ideaal. Onthoud: na −50% heb je +100% nodig om terug te komen op je oude niveau.',
    benchmark: 'S&P 500: correcties van −10% komen bijna elk jaar voor, −20% elke paar jaar; crises gaven −34% (2020) en −55% (2008–09). Boven −25% tot −30% voelt voor de meeste beleggers pijnlijk.',
    note: 'Hefboom (geleend geld) en veel short puts maken drawdowns veel dieper dan bij de index.',
  },
  current_drawdown: {
    title: 'Huidige drawdown',
    what: 'Hoeveel procent je portefeuille nu lager staat dan zijn hoogste stand in de gekozen periode. Met "hoogste punt" bedoelen we de beste dag van die periode: de dag waarop de lijn in de grafiek het hoogst stond (stortingen en opnames tellen niet mee). Staat de lijn vandaag op die top, dan is je drawdown 0%. Kies je een andere periode, dan verandert ook de top: bij "MTD" is het de top van deze maand, bij "YTD" de top sinds 1 januari, bij "All" de beste stand ooit.',
    example: 'De lijn piekte op 12 oktober op 120 en staat nu op 105. Je huidige drawdown is 105 ÷ 120 − 1 = −12,5%. Om weer op de top te komen heb je +14,3% nodig.',
    direction: 'zero',
    good: 'Het getal staat met een minteken omdat het een daling is. Negatief is dus niet "goed": hoe kleiner de min, hoe beter, en 0% betekent dat je op je hoogste stand staat. Is de huidige drawdown gelijk aan de maximale, dan zit je nu in het dal.',
    benchmark: 'Een herstel van −10% vraagt +11%, van −20% vraagt +25%, van −30% vraagt +43%.',
  },
  calmar: {
    title: 'Calmar-ratio',
    what: 'Jaarrendement gedeeld door de maximale drawdown. Het laat zien of het rendement opweegt tegen de ergste daling.',
    direction: 'higher',
    good: 'Onder 0,5: zwak. 0,5–1: redelijk. 1–3: goed. Boven 3: uitstekend.',
    benchmark: 'S&P 500 over de lange termijn rond 0,2–0,4 (≈10% rendement tegenover −55% in de crisis). Succesvolle hedgefondsen halen 1–2.',
    note: 'Wordt pas getoond bij 90+ dagen data; korter geeft een misleidend jaarcijfer.',
  },
  best_worst_day: {
    title: 'Beste en slechtste dag',
    what: 'Het hoogste en laagste dagrendement in deze periode (zonder invloed van stortingen).',
    direction: 'info',
    good: 'Een groot verschil tussen beste en slechtste dag betekent een grillige portefeuille. De slechtste dag is een goede maatstaf voor wat je op een slechte dag moet kunnen verdragen.',
    benchmark: 'Bij de S&P 500 zijn dagen van ±2–3% zeldzaam en ±5% of meer komt alleen in crises voor.',
  },
  risk_free: {
    title: 'Risk-free % (risicovrije rente)',
    what: 'De rente die je zonder risico had kunnen krijgen. Wordt van je rendement afgetrokken in de Sharpe-ratio.',
    direction: 'info',
    good: 'Vul de actuele spaarrente of de 3-maands staatsobligatierente in. Met 0 wordt de Sharpe-ratio iets hoger dan "eerlijk".',
  },
  th_daily_result: {
    title: 'Dagresultaat',
    what: 'Het tijdgewogen rendement van die ene dag, dus zonder effect van stortingen en opnames.',
    direction: 'info',
    good: 'Groen/plus is een winstdag, rood/min een verliesdag.',
  },
  th_cumulative_twr: {
    title: 'Cumulatieve TWR',
    what: 'Het opgetelde (samengestelde) rendement vanaf het begin van de gekozen periode tot en met die dag.',
    direction: 'higher',
    good: 'Dit is de lijn uit de grafiek, als percentage.',
  },
  th_equity: {
    title: 'Equity',
    what: 'De totale waarde van je rekening(en) aan het einde van die dag, inclusief stortingen en opnames. Daarom kan equity stijgen terwijl je rendement daalt.',
    direction: 'info',
  },

  /* ------------------------------ Risk: tabs ------------------------------ */
  tab_overview: {
    title: 'Overzicht',
    what: 'Alle risicokaarten: drawdown-stop, conflicten, blootstelling tegenover je limieten, je limieten zelf en kapitaalefficiëntie.',
    direction: 'info',
  },
  tab_pretrade: {
    title: 'Pre-trade check',
    what: 'Test een order voordat je hem plaatst: de order wordt tijdelijk bij je huidige posities opgeteld en gecontroleerd op je limieten en conflicten. Er wordt niets geplaatst.',
    direction: 'info',
  },
  refresh: {
    title: 'Verversen',
    what: 'Haalt de risicocijfers opnieuw op. De posities komen uit de laatste import (07:00 en 09:00); aandelenprijzen uit de laatste prijsupdate.',
    direction: 'info',
  },

  /* ------------------------ Risk: drawdown-stop ------------------------ */
  dd_stop: {
    title: 'Maandelijkse drawdown-stop',
    what: 'Een noodrem: je stelt in hoeveel procent je portefeuille in een maand maximaal mag zakken vanaf de hoogste stand van die maand. Wordt de grens gehaald, dan krijg je een Telegram-melding en is het tijd om te stoppen met nieuwe risico\'s.',
    direction: 'within',
    good: 'Blijf onder je limiet. Boven 75% van de limiet is het "bijna", boven 100% is de stop geraakt.',
    benchmark: 'Veel optieverkopers hanteren 5–10% per maand; wie met hefboom werkt kiest eerder het strakkere einde.',
    note: 'Gebaseerd op end-of-day IBKR-cijfers zonder stortingen en opnames, dus loopt tot een dag achter. De limiet stel je in onder Settings → Goals.',
  },
  dd_drawdown: {
    title: 'Drawdown vanaf maandhoogte',
    what: 'Hoeveel procent je portefeuille nu onder het hoogste punt van deze maand zit. Het "hoogste punt" is de beste dag sinds het begin van de maand; de slotstand van vorige maand telt als startpunt. Zit je nu op die top, dan is het 0%.',
    example: 'Vorige maand sloot op 100, de beste dag van deze maand was 104. Je staat nu op 95: 95 ÷ 104 − 1 = −8,7%. Met een limiet van 10% zit je dicht bij de stop.',
    direction: 'zero',
    good: 'Het minteken betekent een daling. Hoe kleiner de min, hoe beter (0% = je staat op de hoogste stand van de maand). Vergelijk met je limiet: bij −8% met een limiet van 10% zit je dicht bij de stop.',
  },
  dd_mtd: {
    title: 'Maand tot nu (MTD)',
    what: 'Het rendement sinds de laatste slotstand van vorige maand, zonder stortingen en opnames.',
    direction: 'higher',
    good: 'Positief = winst deze maand. Dit kan beter zijn dan de drawdown als je maandhoog al is gezakt.',
  },
  dd_limit: {
    title: 'Limiet drawdown-stop',
    what: 'De grens die je zelf hebt gekozen. Leeg betekent: geen stop, geen melding.',
    direction: 'info',
    good: 'Kies een grens waarbij je nog rustig kunt handelen, niet de grens waarbij je al in paniek bent.',
    benchmark: 'Gebruikelijk: 5–10% per maand.',
  },

  /* --------------------------- Risk: conflicten --------------------------- */
  conflicts: {
    title: 'Conflicten',
    what: 'Combinaties van posities die tegen elkaar werken of waarbij je bescherming dreigt te verdwijnen. De pagina controleert er twee: een short call onder een short put, en een hedge die binnen 3 dagen afloopt.',
    direction: 'lower',
    good: 'Nul conflicten is de bedoeling. Elk conflict is een reden om je posities te bekijken.',
    note: 'Een "hedge" is hier elke gekochte (long) put. Gebaseerd op de laatste import.',
  },
  conflict_inversion: {
    title: 'Strike-inversie',
    what: 'Je hebt een short call met een lagere strike dan een short put op hetzelfde aandeel (bijv. call 26, put 34). Worden beide uitgeoefend, dan koop je tegen 34 en lever je tegen 26: een vaststaand verlies van het verschil (8 per aandeel), los van de premie.',
    direction: 'lower',
    good: 'Hoort niet voor te komen; los het op door de call hoger te rollen of de put lager.',
    note: 'Telt per aandeel over al je rekeningen heen; de expiratiedata kunnen verschillen, dan is het risico kleiner maar niet weg.',
  },
  conflict_hedge: {
    title: 'Hedge loopt af',
    what: 'Een gekochte put die je beschermt, verloopt binnen 3 dagen terwijl je nog aandelen of short opties op dat aandeel hebt. Na expiratie ben je onbeschermd.',
    direction: 'lower',
    good: 'Beslis voor expiratie: doorrollen, bewust laten lopen, of de onderliggende positie afbouwen.',
  },

  /* ------------------------ Risk: blootstelling ------------------------ */
  exposure_card: {
    title: 'Blootstelling en limieten',
    what: 'Hoe groot je risico is ten opzichte van je vermogen (NAV), en hoe dat zich verhoudt tot de limieten die je hebt ingesteld.',
    direction: 'within',
    good: 'Alles binnen je limieten. Een oranje label betekent boven 80% van een limiet, rood is erover.',
  },
  nav: {
    title: 'NAV (vermogen)',
    what: 'Net asset value: de totale waarde van al je rekeningen in euro (laatste equity van elk account; Tastytrade-bedragen worden van USD omgerekend). Alle percentages op deze pagina zijn ten opzichte van dit bedrag.',
    direction: 'info',
  },
  put_assignment: {
    title: 'Short-put assignment value',
    what: 'Het bedrag dat je nodig hebt als al je verkochte puts tegelijk worden uitgeoefend: strike × 100 × aantal contracten, opgeteld. Dus hoeveel aandelen je dan moet kopen.',
    direction: 'lower',
    good: 'Lager is veiliger. Bij 100% van je NAV kun je alles uit eigen middelen betalen (cash-secured); daarboven leen je via marge.',
    benchmark: 'Vuistregel voor marge-gebruikers: 50–100% van NAV is gangbaar, boven 150% is agressief. Voor de meeste mensen is 60% of lager rustig.',
    note: 'Het is een worst case: niet alle puts worden tegelijk uitgeoefend, maar in een crash gebeurt dat vaak wel.',
  },
  delta_exposure: {
    title: 'Netto delta-exposure',
    what: 'Hoeveel euro aan aandelen je portefeuille "in feite" bezit: aandelen tellen voor 100%, opties voor delta × aantal × 100 × koers. Short puts en long calls tellen als bezit, short calls en long puts verminderen het.',
    direction: 'within',
    good: 'Dit is de gevoeligheid voor de markt. 100% van je NAV betekent dat je net zo reageert op een marktdaling als iemand die volledig belegd is; boven 100% heb je hefboom; negatief betekent netto short.',
    benchmark: 'Optie-inkomensstrategieën zitten vaak op 50–100% van NAV; boven 150% is hefboom die bij een daling snel pijn doet.',
    note: 'Delta is een momentopname: bij een koersdaling nemen de deltas van je short puts toe (negatieve gamma). Aandelen zonder prijs of opties zonder delta zijn buiten beschouwing gelaten.',
  },
  cushion: {
    title: 'Margin cushion',
    what: 'De buffer tot een margin call: de ruimte die je nog hebt (excess liquidity) als percentage van je vermogen. Bij 0% begint de broker posities te sluiten.',
    direction: 'higher',
    good: 'Hoger is veiliger. In deze app: boven 40% comfortabel, 20–40% let op, onder 20% krap.',
    benchmark: 'Voorzichtige beleggers houden minimaal 30–40% aan. Een daling in de markt eet de cushion snel op, omdat de margin-eis stijgt terwijl je vermogen daalt.',
    note: 'Komt van de uurlijkse IBKR-snapshot; zie ook de Margin-pagina.',
  },
  th_shares: {
    title: 'Aandelen',
    what: 'De marktwaarde van de aandelen die je in dit fonds/aandeel bezit, in euro.',
    direction: 'info',
  },
  th_put_assign: {
    title: 'Short-put assignment',
    what: 'Wat je moet betalen als alle verkochte puts op dit aandeel worden uitgeoefend (strike × 100 × contracten).',
    direction: 'lower',
    good: 'Lager is veiliger.',
  },
  th_weight: {
    title: 'Gewicht per aandeel',
    what: 'Het kapitaal dat aan dit ene aandeel vastzit — aandelen plus assignment value van short puts — als percentage van je NAV.',
    direction: 'lower',
    good: 'Lager is gespreider. Het stoplichtje geeft aan dat je boven 80% (oranje) of 100% (rood) van je limiet zit.',
    benchmark: 'Vuistregel: 10–20% per aandeel is gespreid, boven ongeveer 25% is geconcentreerd.',
  },
  th_delta_exposure: {
    title: 'Delta-exposure per aandeel',
    what: 'De netto marktgevoeligheid in euro voor dit aandeel (aandelen + opties met delta). Negatief betekent dat je netto short bent.',
    direction: 'within',
    good: 'Controleer of dit past bij wat je met de positie wilt.',
  },
  th_pct_nav: {
    title: '% van NAV',
    what: 'Het bedrag links, uitgedrukt als percentage van je totale vermogen.',
    direction: 'info',
  },

  /* ----------------------------- Risk: limieten ----------------------------- */
  limits_card: {
    title: 'Jouw limieten',
    what: 'Je eigen spelregels. De pagina markeert oranje boven 80% van een limiet en rood erboven; bij een overschrijding stuurt het systeem één Telegram-melding (opnieuw pas als je eerst weer onder de limiet was).',
    direction: 'within',
    good: 'Een leeg veld betekent: geen limiet. De drawdown-stop stel je in onder Settings → Goals.',
    note: 'De voorbeeldwaarden zijn een voorzichtige start, geen advies.',
  },
  limit_put: {
    title: 'Max. short-put assignment (% van NAV)',
    what: 'Het maximum aan verkochte puts dat je wilt hebben, uitgedrukt in het bedrag dat je bij uitoefening nodig hebt.',
    direction: 'within',
    good: 'Een lagere limiet is voorzichtiger.',
    benchmark: 'Voorstel om mee te beginnen: 60%. Gangbaar: 50–100%; boven 150% is agressief.',
  },
  limit_delta: {
    title: 'Max. netto delta-exposure (% van NAV)',
    what: 'Hoe gevoelig je portefeuille voor de markt mag zijn, in procent van je NAV (de absolute waarde telt, dus ook netto short).',
    direction: 'within',
    good: 'Een lagere limiet is voorzichtiger.',
    benchmark: 'Voorstel om mee te beginnen: 100% (even gevoelig als volledig belegd).',
  },
  limit_name: {
    title: 'Max. gewicht per aandeel (% van NAV)',
    what: 'Het maximale kapitaal (aandelen + short-put assignment value) dat aan één aandeel mag vastzitten.',
    direction: 'within',
    good: 'Een lagere limiet is gespreider.',
    benchmark: 'Voorstel om mee te beginnen: 25%. Vuistregel: 10–20% is gespreid, boven 25% geconcentreerd.',
  },
  limit_cushion: {
    title: 'Min. margin cushion (%)',
    what: 'De ondergrens voor je buffer tot een margin call. Dit is een minimum: je zit eronder als de cushion lager is.',
    direction: 'within',
    good: 'Een hogere ondergrens is voorzichtiger.',
    benchmark: 'Voorstel om mee te beginnen: 25%. In deze app is onder 20% "krap" en boven 40% "comfortabel".',
  },

  /* ----------------------- Risk: kapitaalefficiëntie ----------------------- */
  eff_card: {
    title: 'Kapitaalefficiëntie',
    what: 'Hoeveel premie je per dag verdient (theta) in verhouding tot het bedrag dat je moet kunnen betalen als de optie wordt uitgeoefend. Zo zie je welke posities je kapitaal goed benutten.',
    direction: 'info',
    good: 'Hoger rendement per dag is aantrekkelijk, maar hoort bij meer risico: de strike ligt dichter bij de koers of het aandeel is volatiel.',
  },
  eff_assignment: {
    title: 'Assignment value',
    what: 'Strike × 100 × contracten voor al je verkochte opties: het bedrag dat in het spel is bij uitoefening. USD-posities zijn omgerekend naar euro.',
    direction: 'lower',
    good: 'Lager is minder kapitaal in het spel.',
  },
  eff_theta: {
    title: 'Theta per dag',
    what: 'Hoeveel euro per dag de opties aan tijdswaarde verliezen — jouw verdiensten als verkoper, mits de koers niet beweegt.',
    direction: 'higher',
    good: 'Hoger is meer dagelijkse inkomsten, maar bekijk het samen met het risico.',
    note: 'Theta komt van marketdata.app; posities zonder theta zijn buiten beschouwing gelaten en worden vermeld.',
  },
  eff_daily: {
    title: 'Efficiëntie per dag',
    what: 'Theta per dag gedeeld door de assignment value. 0,10% per dag betekent dat je kapitaal dagelijks 0,10% premie oplevert.',
    direction: 'higher',
    good: 'Hoger is een beter rendement op het vastgezette kapitaal — tot het punt waarop het risico niet meer in verhouding is.',
  },
  eff_annual: {
    title: 'Geannualiseerd',
    what: 'De efficiëntie per dag × 365: wat je per jaar zou verdienen als alles zo bleef doorlopen. Het is een indicatie, geen belofte: theta loopt niet gelijkmatig en koersverlies is niet meegenomen.',
    direction: 'higher',
    good: 'Hoger = meer premie voor het kapitaal, maar zeker boven de 80% moet je je afvragen welk risico je ervoor neemt.',
    benchmark: 'Short puts op rustige large caps: ongeveer 10–25% per jaar; op volatiele groeiaandelen vaak 30–70%.',
  },
  th_contracts: {
    title: 'Contracten',
    what: 'Het aantal opties. Eén contract geeft recht op 100 aandelen.',
    direction: 'info',
  },
  th_pct_total: {
    title: '% van totaal',
    what: 'Het aandeel van dit aandeel in je totale assignment value van verkochte opties.',
    direction: 'lower',
    good: 'Een gelijkmatigere verdeling is gespreider.',
  },
  th_dte: {
    title: 'DTE (dagen tot expiratie)',
    what: 'Hoeveel dagen de optie nog loopt.',
    direction: 'info',
    good: 'Korte looptijd = snellere tijdswaarde-afbouw maar ook meer gamma-risico; veel verkopers kiezen 30–45 dagen.',
  },
  naked: {
    title: 'Naked call',
    what: 'Je hebt minder dan 100 aandelen per verkochte call. Gaat de koers sterk omhoog, dan is je verlies in principe onbeperkt.',
    direction: 'lower',
    good: 'Dek de call met aandelen (covered), of koop een hogere call als bescherming.',
  },

  /* ----------------------------- Risk: stresstest ----------------------------- */
  stress_card: {
    title: 'Stresstest: de markt daalt',
    what: 'Een simulatie van je verlies als alle aandelen tegelijk 10%, 20% of 30% dalen. Aandelen verliezen gewoon die waarde; je opties worden opnieuw gewaardeerd (Black-Scholes) met een hogere volatiliteit, omdat die in een daling meestal stijgt (relatief +10%, +25%, +50%).',
    example: 'Heb je 28.000 NAV en laat de simulatie bij −20% een verlies van 6.000 zien, dan is dat 21% van je NAV.',
    direction: 'lower',
    good: 'Een kleiner verlies is beter. Dit is een eerlijker maatstaf dan de assignment value voor ver uit-de-geldse opties met lange looptijd, omdat het meet wat je op papier verliest en niet het theoretische maximum.',
    benchmark: 'Een volledig belegde portefeuille (delta-exposure 100%) verliest ongeveer evenveel procent als de markt: bij −20% markt zo\'n −20% van je NAV. Verlies boven ongeveer 25–30% van NAV bij een −20% markt wijst op hefboom of veel short puts.',
    note: 'Alle aandelen dalen even hard (bèta 1); risicovolle namen dalen in werkelijkheid vaak harder. Opties zonder volatiliteit zijn benaderd met delta, opties zonder koers overgeslagen; het aantal staat onder de tabel.',
  },
  stress_row: {
    title: 'Verlies bij marktdaling',
    what: 'Het gesimuleerde verlies van je hele portefeuille als de markt met dit percentage daalt (en de volatiliteit stijgt). Het percentage is het verlies als deel van je NAV.',
    direction: 'lower',
    good: 'Lager is beter.',
  },
  limit_stress: {
    title: 'Max. verlies bij −20% markt (% van NAV)',
    what: 'Het grootste verlies dat je bij een marktdaling van 20% (met stijgende volatiliteit) wilt kunnen hebben, als percentage van je NAV. Dit is vaak een betere limiet dan de nominale assignment value, zeker als je ver uit-de-geldse, lang lopende puts verkoopt.',
    direction: 'within',
    good: 'Een lagere limiet is voorzichtiger.',
    benchmark: 'Voorstel om mee te beginnen: 25%. Een volledig belegde portefeuille verliest bij −20% markt ongeveer 20%.',
  },

  /* ----------------------- Risk: feiten over de order ----------------------- */
  ld_pct_otm: {
    title: 'Afstand tot de strike (OTM)',
    what: 'Hoeveel procent de koers moet dalen (bij een put) of stijgen (bij een call) voordat de optie in the money komt.',
    direction: 'higher',
    good: 'Verder uit het geld is veiliger, maar je ontvangt ook minder premie.',
    benchmark: 'Bij korte looptijden (30–45 dagen) kiezen verkopers vaak 5–15% OTM. Bij lange looptijden is verder weg gebruikelijk, omdat de koers meer tijd heeft om te bewegen.',
  },
  ld_prob_itm: {
    title: 'Kans dat hij in the money afloopt',
    what: 'De kans dat het aandeel op de expiratiedatum onder de strike van je put (of boven die van je call) staat, volgens de optiemarkt zelf: berekend met de volatiliteit die in de optieprijs zit. Dit is niet hetzelfde als delta: bij lange looptijden is de kans een stuk groter dan de delta.',
    direction: 'lower',
    good: 'Lager is minder kans op uitoefening op de expiratiedatum.',
    benchmark: 'Verkopers van korte puts kiezen vaak een kans van 15–30%. Bij een looptijd van meerdere jaren is die kans bij dezelfde delta veel hoger.',
    note: 'Dit is de door de markt geïmpliceerde kans, geen voorspelling; voor een groeiaandeel is de werkelijke kans meestal wat lager. En je hoeft niet tot expiratie te wachten: de optie kan eerder duur worden (zie de stresstest).',
  },
  ld_premium: {
    title: 'Premie (mid)',
    what: 'Het midden tussen bid en ask van het contract, per aandeel in dollars. Het totaal in euro is premie × 100 × aantal contracten.',
    direction: 'info',
    note: 'Je uitvoering ligt tussen bid en ask. Bij lang lopende opties is die spreiding vaak groot, dus het verschil met de mid kan flink zijn.',
  },
  ld_breakeven: {
    title: 'Break-even',
    what: 'De koers waaronder je bij expiratie echt verlies maakt: strike min ontvangen premie (put), of strike plus premie (call).',
    direction: 'info',
    good: 'Hoe verder onder de huidige koers (put), hoe meer ruimte je hebt.',
  },
  ld_yield: {
    title: 'Premie per jaar op de strike',
    what: 'De ontvangen premie als percentage van de strike, omgerekend naar een jaar (premie ÷ strike ÷ looptijd in jaren). Dat is wat het vastgezette bedrag per jaar oplevert als de optie waardeloos afloopt.',
    direction: 'higher',
    good: 'Hoger is beter, maar hoort bij meer risico (strike dichter bij de koers).',
    benchmark: 'Ter vergelijking: de risicovrije rente is ongeveer 3–4% per jaar. Lang lopende, ver uit-de-geldse puts leveren vaak maar 1–5% per jaar op de strike; kortlopende puts dicht bij de koers 15–40%.',
    note: 'Dit rekent met de volledige strike. Met margin hoeft maar een deel onderpand te zijn, dus het rendement op je werkelijke margin is hoger. Dat zie je in TWS.',
  },
  ld_max_loss: {
    title: 'Maximaal verlies',
    what: 'Wat je kwijt bent als het aandeel naar nul gaat: (strike − premie) × 100 × contracten, in euro.',
    direction: 'lower',
    good: 'Lager is beter.',
    note: 'Dit is het theoretische maximum. Het laat de ergste uitkomst zien, maar de kans erop is bij kwaliteitsaandelen zeer klein.',
  },
  ld_theta: {
    title: 'Theta per dag',
    what: 'Hoeveel euro per dag je aan tijdswaarde verdient op deze order, mits de koers niet beweegt.',
    direction: 'higher',
    good: 'Hoger is meer dagelijkse inkomsten.',
  },
  ld_delta: {
    title: 'Delta van dit contract',
    what: 'Opgehaald bij marketdata.app op basis van de actuele quote. Put tussen −1 en 0, call tussen 0 en 1.',
    direction: 'info',
  },

  /* --------------------------- Risk: pre-trade check --------------------------- */
  pretrade_card: {
    title: 'Pre-trade check',
    what: 'Voer de order in die je overweegt. Die wordt tijdelijk bij je huidige posities geteld en getoetst aan je limieten en de conflictregels. Er wordt niets geplaatst.',
    direction: 'info',
    note: 'Het margin-effect is niet meegenomen; controleer dat in TWS voor je de order verstuurt.',
  },
  pt_delta: {
    title: 'Delta (optioneel)',
    what: 'De delta van de optie: hoeveel de optieprijs beweegt als het aandeel 1 beweegt (call tussen 0 en 1, put tussen −1 en 0). Laat je het leeg, dan wordt hij automatisch opgehaald bij marketdata.app op basis van de actuele quote van precies dit contract (strike en expiratie). Vul je hem zelf in, dan gebruikt het systeem jouw waarde.',
    direction: 'info',
    good: 'Een delta van 0,30 betekent grofweg 30% kans dat de optie in the money afloopt en 30 aandelen aan equivalent risico per contract.',
    benchmark: 'Verkopers van puts of calls kiezen vaak delta 0,15–0,30.',
    note: 'Na "Check trade" zie je de opgehaalde delta onder het veld staan, samen met de volatiliteit en premie. Bestaat het contract niet (bijvoorbeeld een strike die niet genoteerd is), dan zie je welke strikes wel bestaan; lukt het ophalen even niet, dan staat er een waarschuwing en telt de delta-exposure van dat been niet mee.',
  },
  pt_verdict: {
    title: 'Oordeel',
    what: 'Binnen je limieten: niets wordt erger. Voorzichtig: een limiet komt dichtbij (80%) of er ontstaat een conflict. Overschrijding: een limiet wordt gehaald of verder overschreden, of er ontstaat een ernstig conflict (zoals een strike-inversie). Niet te beoordelen: het contract bestaat niet zoals ingevoerd, of er is geen vermogen gevonden.',
    direction: 'info',
  },
};
